import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import { MOBILE_ORDER_DISPLAY_ONLY_EDITED } from 'features/Store/constants';
import { COLUMN_WIDTH } from 'features/Store/pages/StoreSettingDetailPage';
import {
  selectStoreSettingDetailDisplayTimingSource,
  selectStoreSettingDetailDisplayType,
  storeSettingDetailActions,
} from 'features/Store/storeSettingDetailSlice';
import { getDefaultMobileOrderDisplayFlg, hasEditedMobileOrderDisplayFlg } from 'features/Store/utils';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { MenuDetail, UnAssignedMenu } from '../../models';
import MobileOrderDisplayModal from '../MobileOrderDisplayModal';

export interface UnAssignedMenuRowProps {
  menu: UnAssignedMenu;
}

interface MobileOrderDisplayModalData {
  posCodeId: number;
  posCode: string;
  restaurantId: number;
  menuName: string;
  menuCode: string;
  couponClassification: number;
  defaultMobileOrderDisplayFlg: boolean;
}

function UnAssignedMenuRow({ menu }: UnAssignedMenuRowProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const displayType = useAppSelector(selectStoreSettingDetailDisplayType);
  const displayTimingSource = useAppSelector(selectStoreSettingDetailDisplayTimingSource);

  const [showMobileOrderDisplayModal, setShowMobileOrderDisplayModal] = useState(false);
  const [mobileOrderDisplayModalData, setMobileOrderDisplayModalData] = useState<MobileOrderDisplayModalData>();

  const pathId = `unassigned-menu-${menu.id}`;

  const unAssingedMenuDetailList = menu.menu_details.filter(
    (menuDetail) => menuDetail.counter_pattern_display_flg !== null
  );

  const hasEdited = hasEditedMobileOrderDisplayFlg(unAssingedMenuDetailList[0]);
  const defaultMobileOrderDisplayFlg = getDefaultMobileOrderDisplayFlg(unAssingedMenuDetailList[0]);

  function handleCloseMobileOrderDisplayModal(isDataChanged: boolean = false) {
    if (isDataChanged) {
      dispatch(storeSettingDetailActions.reloadData());
    }

    setShowMobileOrderDisplayModal(false);
    setMobileOrderDisplayModalData(undefined);
  }

  function handleOpenMObileOrderDisplayModal(menuDetail?: MenuDetail) {
    if (menuDetail) {
      const currentDefaultMobileOrderDisplayFlg = getDefaultMobileOrderDisplayFlg(menuDetail);
      setShowMobileOrderDisplayModal(true);
      setMobileOrderDisplayModalData({
        posCodeId: menuDetail.pos_code_id,
        posCode: menuDetail.pos_code_cd,
        restaurantId: Number(restaurantId) || 0,
        menuName: menu.name,
        menuCode: menu.cd,
        couponClassification: menu.coupon_classification,
        defaultMobileOrderDisplayFlg: currentDefaultMobileOrderDisplayFlg || false,
      });
    }
  }

  return (
    <>
      <div id={`menu-${pathId}-row`} className={`dan-tr content-visibility-auto ${hasEdited ? 'has-edited' : ''}`}>
        <div
          className="dan-td"
          style={{
            width: `${COLUMN_WIDTH[0] + COLUMN_WIDTH[1] + COLUMN_WIDTH[2] + COLUMN_WIDTH[3]}%`,
          }}
        ></div>
        <div className="dan-td" style={{ width: `${COLUMN_WIDTH[4]}%` }}>
          {menu.name} ({displayTimingSource?.[menu.display_timing]})
        </div>
        <div className="dan-td justify-content-center" style={{ width: `${COLUMN_WIDTH[5]}%` }}>
          {unAssingedMenuDetailList[0] && Number(unAssingedMenuDetailList[0].pos_code_cd)}
        </div>
        <div className="dan-td justify-content-center" style={{ width: `${COLUMN_WIDTH[6]}%` }}>
          {defaultMobileOrderDisplayFlg === undefined ? null : (
            <Button
              className={clsx('btn-edit-mobile-display-flg', {
                'btn--dark-green': defaultMobileOrderDisplayFlg,
                'btn--green': !defaultMobileOrderDisplayFlg,
              })}
              onClick={handleOpenMObileOrderDisplayModal.bind(null, unAssingedMenuDetailList[0])}
            >
              {defaultMobileOrderDisplayFlg ? t('StoreSettingDetailPage.enable') : t('StoreSettingDetailPage.disable')}
            </Button>
          )}
        </div>
        <div
          className="dan-td"
          style={{
            width: `${COLUMN_WIDTH.slice(7, 17).reduce((sum, width) => sum + width, 0)}%`,
          }}
        ></div>
      </div>
      {unAssingedMenuDetailList.map(function (menuDetail, index) {
        if (index === 0) {
          return null;
        }

        const hasEdited = hasEditedMobileOrderDisplayFlg(menuDetail);
        const otherDefaultMobileOrderDisplayFlg = getDefaultMobileOrderDisplayFlg(menuDetail);

        if (displayType === MOBILE_ORDER_DISPLAY_ONLY_EDITED && !hasEdited) {
          return null;
        }

        return (
          <div key={index} id={`menu-${pathId}-detail-row`} className={`dan-tr ${hasEdited ? 'has-edited' : ''}`}>
            <div
              className="dan-td"
              style={{
                width: `${COLUMN_WIDTH[0] + COLUMN_WIDTH[1] + COLUMN_WIDTH[2] + COLUMN_WIDTH[3] + COLUMN_WIDTH[4]}%`,
              }}
            ></div>
            <div className="dan-td justify-content-center" style={{ width: `${COLUMN_WIDTH[5]}%` }}>
              {Number(menuDetail.pos_code_cd)}
            </div>
            <div className="dan-td justify-content-center" style={{ width: `${COLUMN_WIDTH[6]}%` }}>
              {otherDefaultMobileOrderDisplayFlg === undefined ? null : (
                <Button
                  className={clsx('btn-edit-mobile-display-flg', {
                    'btn--dark-green': otherDefaultMobileOrderDisplayFlg,
                    'btn--green': !otherDefaultMobileOrderDisplayFlg,
                  })}
                  onClick={handleOpenMObileOrderDisplayModal.bind(null, menuDetail)}
                >
                  {otherDefaultMobileOrderDisplayFlg
                    ? t('StoreSettingDetailPage.enable')
                    : t('StoreSettingDetailPage.disable')}
                </Button>
              )}
            </div>
            <div
              className="dan-td"
              style={{
                width: `${COLUMN_WIDTH.slice(7, 17).reduce((sum, width) => sum + width, 0)}%`,
              }}
            ></div>
          </div>
        );
      })}

      {showMobileOrderDisplayModal &&
        mobileOrderDisplayModalData &&
        createPortal(
          <MobileOrderDisplayModal
            posCodeId={mobileOrderDisplayModalData.posCodeId}
            posCode={mobileOrderDisplayModalData.posCode}
            restaurantId={mobileOrderDisplayModalData.restaurantId}
            menuName={mobileOrderDisplayModalData.menuName}
            menuCode={mobileOrderDisplayModalData.menuCode}
            couponClassification={mobileOrderDisplayModalData.couponClassification}
            defaultMobileOrderDisplayFlg={mobileOrderDisplayModalData.defaultMobileOrderDisplayFlg}
            onClose={handleCloseMobileOrderDisplayModal}
          />,
          document.body
        )}
    </>
  );
}

export default UnAssignedMenuRow;
